import {
	Ornt,
	ListWidget,
	Widget,
	WidgetStack,
	WidgetText,
	widgetType,
	WidgetImage,
	WidgetSpacer,
	WidgetDate,
} from '../types';
import getTextSize from './getTextSize';

interface Context {
	parentAlign: Ornt;
	maxSize: {
		// 允许子元素的最大长度
		[Ornt.X]: number;
		[Ornt.Y]: number;
	};
}

// TODO：会出现上下跳动的情况
/* 	stack:
		image
		text...
	image
	text
 */
function apportion(widget: Widget, context: Context) {
	const handlers: any = {
		/* stack */
		[widgetType.STACK]: (widget: WidgetStack, context: Context) => {
			/**
			 * 分配的实现思路：先根据每种情况写出代码，然后进行合并
			 * padding的实现思路：
			 * 	① 手动设定尺寸的容器：固定尺寸减去padding为可分配尺寸，容器尺寸为固定尺寸
			 * 	② 非手动设定尺寸的容器：最大允许尺寸减去padding为可分配尺寸，容器尺寸为分配后尺寸加上padding
			 * 	③ satck容器需设置css属性box-sizing为border-box
			 */

			// 手动设置的尺寸
			const fixedSize = {
				[Ornt.X]: widget.attrs.size.width,
				[Ornt.Y]: widget.attrs.size.height,
			};
			// 计算出当前容器的最大允许宽高，作为分配子元素的依据
			const myMaxSize = {
				[Ornt.X]: fixedSize[Ornt.X] || context.maxSize[Ornt.X],
				[Ornt.Y]: fixedSize[Ornt.Y] || context.maxSize[Ornt.Y],
			};
			// 可分配尺寸减去padding
			const paddingSize = {
				[Ornt.X]: widget.attrs.padding.left + widget.attrs.padding.right,
				[Ornt.Y]: widget.attrs.padding.top + widget.attrs.padding.bottom,
			};
			myMaxSize.width -= paddingSize.width;
			myMaxSize.height -= paddingSize.height;
			if (myMaxSize.width < 0) myMaxSize.width = 0;
			if (myMaxSize.height < 0) myMaxSize.height = 0;

			const myAlign = widget.attrs.layout;
			const myOtherAlign = myAlign === Ornt.X ? Ornt.Y : Ornt.X;
			const parentAlign = context.parentAlign;
			// 对子元素进行分配（不包括无约束的spacer元素）
			const nodesCopy = widget.showNodes.map(n => n);
			const spacerArray: Array<WidgetSpacer> = [];
			let hasFixed = true;
			let lastLen = myMaxSize[myAlign];
			const myCtx = {
				parentAlign: myAlign,
				maxSize: { width: 0, height: 0 },
			};
			let isFixed: boolean;
			while (hasFixed) {
				hasFixed = false;
				myCtx.maxSize[myAlign] = lastLen / nodesCopy.length;
				myCtx.maxSize[myOtherAlign] = myMaxSize[myOtherAlign];

				nodesCopy.forEach((node, index, array) => {
					isFixed = apportion(node, myCtx);
					if (isFixed) {
						lastLen -= node.size[myAlign];
						array.splice(index, 1);
						hasFixed = true;
						return;
					}
					if (node.type === widgetType.SPACER) {
						array.splice(index, 1);
						spacerArray.push(node as WidgetSpacer);
						hasFixed = true;
					}
				});
			}
			// 依据所有子元素的宽高，决定当前容器的尺寸
			const myRealSize = {
				[Ornt.X]: 0,
				[Ornt.Y]: 0,
			};
			widget.showNodes.forEach(node => {
				myRealSize[myAlign] += node.size[myAlign];
				if (node.size[myOtherAlign] > myRealSize[myOtherAlign]) {
					myRealSize[myOtherAlign] = node.size[myOtherAlign];
				}
			});
			// 对无约束的spacer元素进行分配（因为spacer不参与上面的分配）
			if (spacerArray.length > 0) {
				const avgLen = (myMaxSize[myAlign] - myRealSize[myAlign]) / spacerArray.length;
				spacerArray.forEach(spacer => {
					spacer.size[myAlign] = avgLen;
					myRealSize[myAlign] += avgLen;
				});
			}
			// 将减去的padding加回去
			widget.size[myAlign] = fixedSize[myAlign] || myRealSize[myAlign] + paddingSize[myAlign] || 50;
			widget.size[myOtherAlign] =
				fixedSize[myOtherAlign] || myRealSize[myOtherAlign] + paddingSize[myOtherAlign] || 50;
			// 依据当前容器所得到的尺寸，决定该容器是固定元素，还是自由元素
			if (fixedSize[parentAlign]) return true;

			// 处理其中采用fill模式图片的情况
			if (
				widget.realNodes.some(node => {
					if (node.type === widgetType.IMAGE) {
						return (node as WidgetImage).attrs.contentMode === 'fill';
					}
					return false;
				})
			) {
				return false;
			}
			// 	todo：用取整的方法简单处理计算误差造成的BUG
			if (Math.floor(myRealSize[myOtherAlign]) !== Math.floor(context.maxSize[parentAlign])) return true;
			return false;
		},
		/* text */
		[widgetType.TEXT]: (widget: WidgetText | WidgetDate, context: Context) => {
			const parentAlign = context.parentAlign;
			const otherAlign = parentAlign == Ornt.X ? Ornt.Y : Ornt.X;
			const text = (widget as WidgetText).attrs.text || (widget as WidgetDate).text;
			const fontSize = widget.attrs.font.size;

			const assignLen = context.maxSize[parentAlign];
			const lineLimitLen = fontSize * widget.attrs.lineLimit;
			const computedLen = getTextSize(text, parentAlign, { [otherAlign]: context.maxSize[otherAlign] }, fontSize);
			let realLen = computedLen;
			if (parentAlign === Ornt.Y && lineLimitLen > 0) {
				realLen = Math.min(computedLen, lineLimitLen);
			}

			// 按固定元素分配
			if (realLen < assignLen) {
				widget.size[otherAlign] = getTextSize(
					text,
					otherAlign,
					{
						[otherAlign]: context.maxSize[otherAlign],
						[parentAlign]: realLen,
					},
					fontSize
				);
				widget.size[parentAlign] = realLen;
				return true;
			}

			// 按自由元素分配
			const otherAlignRealLen = getTextSize(text, otherAlign, context.maxSize, fontSize);
			const otherAlignAssignLen = context.maxSize[otherAlign];
			if (parentAlign === Ornt.X && lineLimitLen > 0) {
				widget.size[otherAlign] = Math.min(otherAlignRealLen, otherAlignAssignLen, lineLimitLen);
			} else {
				widget.size[otherAlign] = Math.min(otherAlignRealLen, otherAlignAssignLen);
			}
			widget.size[parentAlign] = assignLen;
			return false;
		},
		/* date */
		[widgetType.DATE]: (widget: WidgetDate, context: Context) => {
			return handlers[widgetType.TEXT](widget, context);
		},
		/* image */
		[widgetType.IMAGE]: (widget: WidgetImage, context: Context) => {
			const parentAlign = context.parentAlign;
			const otherAlign = parentAlign === Ornt.X ? Ornt.Y : Ornt.X;
			const rate = widget.rawImageSize[parentAlign] / widget.rawImageSize[otherAlign];

			// 按手动设置的尺寸分配
			const fixedHeight = widget.attrs.imageSize[Ornt.Y];
			const fixedWidth = widget.attrs.imageSize[Ornt.X];
			if (fixedHeight > 0 || fixedWidth > 0) {
				widget.size[Ornt.X] = fixedWidth;
				widget.size[Ornt.Y] = fixedHeight;
				return true;
			}

			// 如果图片不能缩放，则保持原始尺寸
			if (!widget.attrs.resizable) {
				widget.size[Ornt.X] = widget.rawImageSize[Ornt.X];
				widget.size[Ornt.Y] = widget.rawImageSize[Ornt.Y];
				return true;
			}

			// 如果图片采用fill模式填充
			if (widget.attrs.contentMode === 'fill') {
				let maxLen = rate * context.maxSize[otherAlign];
				if (maxLen > context.maxSize[parentAlign]) {
					widget.size[otherAlign] = context.maxSize[otherAlign];
					widget.size[parentAlign] = maxLen;
				} else {
					maxLen = (1 / rate) * context.maxSize[parentAlign];
					widget.size[otherAlign] = maxLen;
					widget.size[parentAlign] = context.maxSize[parentAlign];
				}
				return false;
			}

			// 按固定元素分配
			const realLen = rate * context.maxSize[otherAlign];
			if (realLen <= context.maxSize[parentAlign]) {
				widget.size[otherAlign] = context.maxSize[otherAlign];
				widget.size[parentAlign] = realLen;
				return true;
			}
			// 按自由元素分配
			widget.size[otherAlign] = (1 / rate) * context.maxSize[parentAlign];
			widget.size[parentAlign] = context.maxSize[parentAlign];
			return false;
		},
		/* spacer */
		[widgetType.SPACER]: (widget: WidgetSpacer, context: Context) => {
			const parentAlign = context.parentAlign;
			const otherAlign = parentAlign === Ornt.X ? Ornt.Y : Ornt.X;
			const fixedLen = Number(widget.attrs.length);
			if (fixedLen) {
				widget.size[parentAlign] = fixedLen;
				widget.size[otherAlign] = 0;
				return true;
			}
			widget.size[parentAlign] = 0;
			widget.size[otherAlign] = 0;
			return false;
		},
	};

	return handlers[widget.type](widget, context);
}

// 入口
export default (widget: ListWidget) => {
	const myHeight = widget.size.height - widget.attrs.padding.top - widget.attrs.padding.bottom;
	const myWidth = widget.size.width - widget.attrs.padding.left - widget.attrs.padding.right;

	const childrenCopy: Array<Widget> = widget.showNodes.map(n => n);
	const spacerArray: Array<WidgetSpacer> = [];
	let hasFixed = true;
	let lastLen = myHeight;
	let context: Context;
	let isFixed: boolean;
	while (hasFixed) {
		hasFixed = false;
		context = {
			parentAlign: Ornt.Y,
			maxSize: {
				[Ornt.X]: myWidth,
				[Ornt.Y]: lastLen / childrenCopy.length,
			},
		};
		childrenCopy.forEach((child, index, array) => {
			isFixed = apportion(child, context);
			if (isFixed) {
				lastLen -= child.size[Ornt.Y];
				array.splice(index, 1);
				hasFixed = true;
				return;
			}
			if (child.type === widgetType.SPACER) {
				array.splice(index, 1);
				spacerArray.push(child as WidgetSpacer);
				hasFixed = true;
			}
		});
	}

	let totalHeight = 0;
	widget.showNodes.forEach(node => {
		totalHeight += node.size.height;
	});
	if (spacerArray.length > 0) {
		const avgLen = (myHeight - totalHeight) / spacerArray.length;
		spacerArray.forEach(spacer => {
			spacer.size[Ornt.Y] = avgLen;
			spacer.size[Ornt.X] = 0;
		});
	}
};
